import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Download, FileSpreadsheet, History, Upload, UserCheck, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/auth-provider';
import { fetchPaginated } from '@/lib/supabase-utils';
import { calculateSessionHours } from '@/lib/time-utils';
import { getQatarDayRange } from '@/utils/timezone';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { downloadSpreadsheet, readSpreadsheetObjects } from '@/lib/spreadsheet-browser';

const DRAFT_COLUMNS = [
  'Staff ID',
  'Employee Name',
  'Department',
  'Position',
  'Basic Salary',
  'Hourly Rate',
  'Tracked Hours',
  'Overtime Hours',
  'Deduction Hours',
  'Deduction Amount',
  'Net Salary',
];

interface EmployeeRow {
  id: string;
  email: string;
  full_name: string;
  role: string;
  organization_id: string | null;
  staff_id?: string | null;
  department?: string | null;
  position?: string | null;
  salary_amount?: number | null;
}

interface SalarySettingsRow {
  user_id: string;
  salary_type: string;
  hourly_rate: number | null;
  monthly_salary: number | null;
  minimum_hours_monthly: number | null;
  overtime_rate: number | null;
}

interface DraftPayrollRow {
  row_number: number;
  staff_id: string;
  user_id: string | null;
  employee_name: string;
  department: string;
  position: string;
  basic_salary: number;
  hourly_rate: number;
  tracked_hours: number;
  overtime_hours: number;
  deduction_hours: number;
  deduction_amount: number;
  net_salary: number;
  match_status: 'matched' | 'unmatched';
  raw_row: Record<string, unknown>;
}

interface PayrollUpload {
  id: string;
  month_year: string;
  file_name: string;
  original_row_count: number;
  status: 'draft' | 'approved' | 'rejected';
  created_at: string;
  approved_at?: string | null;
}

interface TimesheetRow {
  id: string;
  start_time: string;
  end_time: string | null;
  deducted_seconds?: number | null;
  is_idle?: boolean | null;
}

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
const toNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};
const round2 = (value: number) => Math.round(value * 100) / 100;

function getCell(row: Record<string, unknown>, aliases: string[]): unknown {
  const map = new Map(Object.keys(row).map((key) => [normalize(key), key]));
  for (const alias of aliases) {
    const key = map.get(normalize(alias));
    if (key) return row[key];
  }
  return undefined;
}

function monthBounds(month: string) {
  const firstDay = `${month}-01`;
  const [year, monthNumber] = month.split('-').map(Number);
  const lastDay = `${month}-${String(new Date(Date.UTC(year, monthNumber, 0)).getUTCDate()).padStart(2, '0')}`;
  return {
    start: getQatarDayRange(firstDay).start,
    end: getQatarDayRange(lastDay).end,
  };
}

export default function PayrollPage() {
  const { userDetails, isSuperAdmin } = useAuth();
  const organizationId = userDetails?.organization_id || null;
  const canManagePayroll = Boolean(
    isSuperAdmin ||
    userDetails?.is_super_admin ||
    userDetails?.role === 'hr_admin' ||
    userDetails?.role === 'admin' ||
    userDetails?.is_org_admin
  );

  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [salarySettings, setSalarySettings] = useState<SalarySettingsRow[]>([]);
  const [uploads, setUploads] = useState<PayrollUpload[]>([]);
  const [selectedUploadId, setSelectedUploadId] = useState<string | null>(null);
  const [uploadedPreview, setUploadedPreview] = useState<Record<string, unknown>[]>([]);
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const [totalRowCount, setTotalRowCount] = useState<number>(0);
  const [draftRows, setDraftRows] = useState<DraftPayrollRow[]>([]);
  const [timesheetUserId, setTimesheetUserId] = useState<string | null>(null);
  const [timesheet, setTimesheet] = useState<TimesheetRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedUpload = useMemo(() => uploads.find((upload) => upload.id === selectedUploadId) || null, [uploads, selectedUploadId]);
  const totals = useMemo(() => ({
    staff: draftRows.length,
    matched: draftRows.filter((row) => row.match_status === 'matched').length,
    net: round2(draftRows.reduce((sum, row) => sum + row.net_salary, 0)),
  }), [draftRows]);

  useEffect(() => {
    if (!canManagePayroll) return;
    fetchSetupData();
    fetchUploads();
  }, [canManagePayroll, selectedMonth, organizationId, isSuperAdmin]);

  useEffect(() => {
    if (!selectedUploadId) return;
    fetchUploadRows(selectedUploadId);
  }, [selectedUploadId]);

  const fetchSetupData = async () => {
    try {
      let employeeQuery = (supabase as any)
        .from('users')
        .select('id,email,full_name,role,organization_id,staff_id,department,position,salary_amount')
        .in('role', ['employee', 'admin', 'manager', 'hr_admin'])
        .order('full_name');

      if (organizationId && !isSuperAdmin) employeeQuery = employeeQuery.eq('organization_id', organizationId);
      const [{ data: employeeData, error: employeeError }, { data: salaryData, error: salaryError }] = await Promise.all([
        employeeQuery,
        (supabase as any).from('employee_salary_settings').select('user_id,salary_type,hourly_rate,monthly_salary,minimum_hours_monthly,overtime_rate'),
      ]);
      if (employeeError) throw employeeError;
      if (salaryError) throw salaryError;
      setEmployees(employeeData || []);
      setSalarySettings(salaryData || []);
    } catch (error) {
      console.error('Error loading payroll setup:', error);
      toast.error('Failed to load payroll setup data');
    }
  };

  const fetchUploads = async () => {
    try {
      let query = (supabase as any)
        .from('payroll_uploads')
        .select('id,month_year,file_name,original_row_count,status,created_at,approved_at')
        .eq('month_year', `${selectedMonth}-01`)
        .order('created_at', { ascending: false });
      if (organizationId && !isSuperAdmin) query = query.eq('organization_id', organizationId);
      const { data, error } = await query;
      if (error) throw error;
      setUploads(data || []);
      if (!selectedUploadId && data?.[0]?.id) setSelectedUploadId(data[0].id);
    } catch (error) {
      console.error('Error loading payroll history:', error);
      toast.error('Failed to load payroll history');
    }
  };

  const fetchUploadRows = async (uploadId: string) => {
    setLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from('payroll_upload_rows')
        .select('*')
        .eq('upload_id', uploadId)
        .order('row_number');
      if (error) throw error;
      setDraftRows((data || []) as DraftPayrollRow[]);
      setUploadedPreview((data || []).map((row: any) => row.raw_row || {}));
    } catch (error) {
      console.error('Error loading payroll rows:', error);
      toast.error('Failed to load uploaded payroll');
    } finally {
      setLoading(false);
    }
  };

  const calculateHours = async (userIds: string[]) => {
    if (userIds.length === 0) return new Map<string, number>();
    const { start, end } = monthBounds(selectedMonth);
    const query = supabase
      .from('time_logs')
      .select('user_id,start_time,end_time')
      .in('user_id', userIds)
      .gte('start_time', start)
      .lte('start_time', end)
      .not('end_time', 'is', null);
    const logs = await fetchPaginated<any>(query);
    const hours = new Map<string, number>();
    logs.forEach((log) => {
      const duration = calculateSessionHours(log.start_time, log.end_time);
      if (duration > 0) hours.set(log.user_id, (hours.get(log.user_id) || 0) + duration);
    });
    return hours;
  };

  const buildDraftRows = async (rawRows: Record<string, unknown>[]) => {
    const employeeByStaffId = new Map(
      employees
        .filter((employee) => employee.staff_id)
        .map((employee) => [String(employee.staff_id).trim().toLowerCase(), employee])
    );
    const matchedUserIds = rawRows
      .map((row) => String(getCell(row, ['Staff ID', 'StaffID', 'Employee ID', 'EmployeeID']) || '').trim().toLowerCase())
      .map((staffId) => employeeByStaffId.get(staffId)?.id)
      .filter(Boolean) as string[];
    const hoursByUser = await calculateHours([...new Set(matchedUserIds)]);

    return rawRows.map((row, index): DraftPayrollRow => {
      const staffId = String(getCell(row, ['Staff ID', 'StaffID', 'Employee ID', 'EmployeeID']) || '').trim();
      const employee = employeeByStaffId.get(staffId.toLowerCase());
      const settings = employee ? salarySettings.find((setting) => setting.user_id === employee.id) : undefined;
      const trackedHoursFromSystem = employee ? round2(hoursByUser.get(employee.id) || 0) : 0;
      const minimumHours = settings?.minimum_hours_monthly || 0;
      const basicSalary = toNumber(getCell(row, ['Basic Salary', 'Basic', 'Salary'])) || Number(settings?.monthly_salary || employee?.salary_amount || 0);
      const hourlyRate = toNumber(getCell(row, ['Hourly Rate', 'HourlyRate', 'Rate'])) || Number(settings?.hourly_rate || (minimumHours ? basicSalary / minimumHours : 0));
      const trackedHours = toNumber(getCell(row, ['Tracked Hours', 'TrackedHours', 'Hours'])) || trackedHoursFromSystem;
      const overtimeHours = toNumber(getCell(row, ['Overtime Hours', 'OvertimeHours', 'OT Hours'])) || Math.max(0, trackedHours - minimumHours);
      const deductionHours = toNumber(getCell(row, ['Deduction Hours', 'DeductionHours', 'Short Hours'])) || Math.max(0, minimumHours - trackedHours);
      const deductionAmount = toNumber(getCell(row, ['Deduction Amount', 'DeductionAmount', 'Deductions'])) || (deductionHours * hourlyRate);
      const overtimeRate = Number(settings?.overtime_rate || hourlyRate);
      const netSalary = toNumber(getCell(row, ['Net Salary', 'NetSalary', 'Final Salary'])) || (basicSalary + overtimeHours * overtimeRate - deductionAmount);

      return {
        row_number: index + 1,
        staff_id: staffId,
        user_id: employee?.id || null,
        employee_name: String(getCell(row, ['Employee Name', 'EmployeeName', 'Name']) || employee?.full_name || ''),
        department: String(getCell(row, ['Department', 'Dept']) || employee?.department || ''),
        position: String(getCell(row, ['Position', 'Job Title', 'Title']) || employee?.position || ''),
        basic_salary: round2(basicSalary),
        hourly_rate: round2(hourlyRate),
        tracked_hours: round2(trackedHours),
        overtime_hours: round2(overtimeHours),
        deduction_hours: round2(deductionHours),
        deduction_amount: round2(deductionAmount),
        net_salary: round2(netSalary),
        match_status: employee ? 'matched' : 'unmatched',
        raw_row: row,
      };
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const rawRows = await readSpreadsheetObjects(file);
      if (rawRows.length === 0) throw new Error('The uploaded sheet is empty');
      setUploadedFileName(file.name);
      setUploadedPreview(rawRows.slice(0, 100));
      setTotalRowCount(rawRows.length);
      const generatedRows = await buildDraftRows(rawRows);
      setDraftRows(generatedRows);
      setSelectedUploadId(null);
      toast.success(`Draft created from ${rawRows.length} uploaded rows`);
    } catch (error: any) {
      console.error('Error reading payroll Excel:', error);
      toast.error(error?.message || 'Failed to read Excel file');
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const saveDraft = async () => {
    if (draftRows.length === 0) return;
    setSaving(true);
    try {
      const { data: upload, error: uploadError } = await (supabase as any)
        .from('payroll_uploads')
        .insert({
          organization_id: organizationId,
          month_year: `${selectedMonth}-01`,
          file_name: uploadedFileName || `payroll-${selectedMonth}.xlsx`,
          original_row_count: totalRowCount,
          status: 'draft',
          uploaded_by: userDetails?.id,
        })
        .select('id')
        .single();
      if (uploadError) throw uploadError;

      const rows = draftRows.map((row) => ({ ...row, upload_id: upload.id, organization_id: organizationId }));
      const { error: rowsError } = await (supabase as any).from('payroll_upload_rows').insert(rows);
      if (rowsError) throw rowsError;

      toast.success('Payroll draft saved for review');
      setSelectedUploadId(upload.id);
      await fetchUploads();
    } catch (error) {
      console.error('Error saving payroll draft:', error);
      toast.error('Failed to save payroll draft');
    } finally {
      setSaving(false);
    }
  };

  const approveDraft = async () => {
    if (!selectedUploadId || selectedUpload?.status !== 'draft') return;
    const unmatchedCount = draftRows.filter((row) => row.match_status === 'unmatched').length;
    if (unmatchedCount > 0) {
      toast.error(`Cannot approve: ${unmatchedCount} unmatched Staff ID(s) must be resolved first.`);
      return;
    }
    setSaving(true);
    try {
      const { error } = await (supabase as any)
        .from('payroll_uploads')
        .update({ status: 'approved', approved_by: userDetails?.id, approved_at: new Date().toISOString() })
        .eq('id', selectedUploadId);
      if (error) throw error;
      toast.success('Payroll draft approved');
      await fetchUploads();
    } catch (error) {
      console.error('Error approving payroll:', error);
      toast.error('Failed to approve payroll');
    } finally {
      setSaving(false);
    }
  };

  const exportDraft = async () => {
    const data = draftRows.map((row) => ({
      'Staff ID': row.staff_id,
      'Employee Name': row.employee_name,
      Department: row.department,
      Position: row.position,
      'Basic Salary': row.basic_salary,
      'Hourly Rate': row.hourly_rate,
      'Tracked Hours': row.tracked_hours,
      'Overtime Hours': row.overtime_hours,
      'Deduction Hours': row.deduction_hours,
      'Deduction Amount': row.deduction_amount,
      'Net Salary': row.net_salary,
    }));
    try {
      await downloadSpreadsheet(
        `draft-payroll-${selectedMonth}.xlsx`,
        'Draft Payroll',
        data,
        DRAFT_COLUMNS,
      );
    } catch (error) {
      console.error('Failed to export payroll draft', error);
      toast.error('Failed to export payroll draft');
    }
  };

  const loadTimesheet = async (userId: string | null) => {
    setTimesheetUserId(userId);
    if (!userId) {
      setTimesheet([]);
      return;
    }
    try {
      const { start, end } = monthBounds(selectedMonth);
      const { data, error } = await supabase
        .from('time_logs')
        .select('id,start_time,end_time,deducted_seconds,is_idle')
        .eq('user_id', userId)
        .gte('start_time', start)
        .lte('start_time', end)
        .order('start_time', { ascending: false })
        .limit(100);
      if (error) throw error;
      setTimesheet(data || []);
    } catch (error) {
      console.error('Error loading timesheet:', error);
      toast.error('Failed to load employee timesheet');
    }
  };

  if (!canManagePayroll) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground">Access denied. Payroll is limited to HR Admin and Owner.</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><FileSpreadsheet className="h-8 w-8" /> Payroll Upload</h1>
          <p className="text-muted-foreground">Upload one master Excel sheet, generate a draft, review it, then approve.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label htmlFor="payroll-month">Payroll Month</Label>
            <Input id="payroll-month" type="month" value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)} className="w-44" />
          </div>
          <div>
            <Label htmlFor="payroll-file">Master Excel</Label>
            <Input id="payroll-file" type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload} className="w-72" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{totals.staff}</div><div className="text-sm text-muted-foreground">Draft staff rows</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-green-600">{totals.matched}</div><div className="text-sm text-muted-foreground">Matched by Staff ID</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-blue-600">{totals.net.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div><div className="text-sm text-muted-foreground">Draft net salary total</div></CardContent></Card>
      </div>

      <Tabs defaultValue="draft" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="upload"><Upload className="h-4 w-4 mr-2" /> Uploaded Excel Preview</TabsTrigger>
          <TabsTrigger value="draft"><FileSpreadsheet className="h-4 w-4 mr-2" /> Draft Payroll Preview</TabsTrigger>
          <TabsTrigger value="history"><History className="h-4 w-4 mr-2" /> Month-wise History</TabsTrigger>
          <TabsTrigger value="timesheet"><Clock className="h-4 w-4 mr-2" /> Employee Timesheet</TabsTrigger>
        </TabsList>

        <TabsContent value="upload">
          <Card>
            <CardHeader><CardTitle>Uploaded Excel Preview</CardTitle><CardDescription>{uploadedFileName ? `${uploadedFileName} • first 100 rows from the uploaded master sheet.` : 'First 100 rows from the uploaded master sheet.'}</CardDescription></CardHeader>
            <CardContent><PreviewTable rows={uploadedPreview} empty="Upload an Excel file to preview it here." /></CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="draft">
          <Card>
            <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div><CardTitle>Draft Payroll Preview</CardTitle><CardDescription>Review all calculated rows before approval. Unmatched Staff IDs must be fixed before final processing.</CardDescription></div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={exportDraft} disabled={!draftRows.length}><Download className="h-4 w-4 mr-2" /> Export Draft</Button>
                <Button variant="outline" onClick={saveDraft} disabled={!draftRows.length || saving}>{saving ? 'Saving...' : 'Save Draft'}</Button>
                <Button onClick={approveDraft} disabled={!selectedUploadId || selectedUpload?.status !== 'draft' || saving}><CheckCircle className="h-4 w-4 mr-2" /> Approve</Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? <div className="text-center py-8">Loading payroll...</div> : <DraftTable rows={draftRows} onTimesheet={loadTimesheet} />}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader><CardTitle>Month-wise Payroll History</CardTitle><CardDescription>Saved payroll drafts and approvals for {format(new Date(`${selectedMonth}-01`), 'MMMM yyyy')}.</CardDescription></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {uploads.length === 0 ? <div className="text-center py-8 text-muted-foreground">No payroll uploads found for this month.</div> : uploads.map((upload) => (
                  <button key={upload.id} onClick={() => setSelectedUploadId(upload.id)} className={`w-full text-left border rounded-lg p-4 hover:bg-blue-50 ${selectedUploadId === upload.id ? 'border-blue-500 bg-blue-50' : ''}`}>
                    <div className="flex justify-between gap-4">
                      <div><div className="font-medium">{upload.file_name}</div><div className="text-sm text-muted-foreground">{upload.original_row_count} rows • Uploaded {format(new Date(upload.created_at), 'dd MMM yyyy HH:mm')}</div></div>
                      <Badge variant={upload.status === 'approved' ? 'default' : 'secondary'}>{upload.status}</Badge>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timesheet">
          <Card>
            <CardHeader><CardTitle>Employee Timesheet</CardTitle><CardDescription>Select any matched draft row to view its linked monthly timesheet.</CardDescription></CardHeader>
            <CardContent>
              {!timesheetUserId ? <div className="text-center py-8 text-muted-foreground">No employee selected.</div> : (
                <div className="overflow-auto border rounded-lg">
                  <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead><TableHead>Hours</TableHead><TableHead>Status</TableHead></TableRow></TableHeader><TableBody>
                    {timesheet.map((log) => (
                      <TableRow key={log.id}><TableCell>{format(new Date(log.start_time), 'dd MMM yyyy')}</TableCell><TableCell>{format(new Date(log.start_time), 'HH:mm')}</TableCell><TableCell>{log.end_time ? format(new Date(log.end_time), 'HH:mm') : '-'}</TableCell><TableCell>{log.end_time ? round2(calculateSessionHours(log.start_time, log.end_time)).toFixed(2) : '0.00'}</TableCell><TableCell>{log.is_idle ? 'Idle' : 'Tracked'}</TableCell></TableRow>
                    ))}
                  </TableBody></Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PreviewTable({ rows, empty }: { rows: Record<string, unknown>[]; empty: string }) {
  const headers = rows[0] ? Object.keys(rows[0]) : [];
  if (!rows.length) return <div className="text-center py-8 text-muted-foreground">{empty}</div>;
  return <div className="overflow-auto border rounded-lg max-h-[520px]"><Table><TableHeader><TableRow>{headers.map((header) => <TableHead key={header}>{header}</TableHead>)}</TableRow></TableHeader><TableBody>{rows.map((row, index) => <TableRow key={index}>{headers.map((header) => <TableCell key={header}>{String(row[header] ?? '')}</TableCell>)}</TableRow>)}</TableBody></Table></div>;
}

function DraftTable({ rows, onTimesheet }: { rows: DraftPayrollRow[]; onTimesheet: (userId: string | null) => void }) {
  if (!rows.length) return <div className="text-center py-8 text-muted-foreground">Upload a master Excel sheet to generate a draft payroll preview.</div>;
  return <div className="overflow-auto border rounded-lg max-h-[620px]"><Table><TableHeader><TableRow>{DRAFT_COLUMNS.map((column) => <TableHead key={column}>{column}</TableHead>)}<TableHead>Status</TableHead><TableHead>Timesheet</TableHead></TableRow></TableHeader><TableBody>{rows.map((row) => <TableRow key={`${row.row_number}-${row.staff_id}`}><TableCell>{row.staff_id || <span className="text-red-600">Missing</span>}</TableCell><TableCell>{row.employee_name}</TableCell><TableCell>{row.department}</TableCell><TableCell>{row.position}</TableCell><TableCell>{row.basic_salary.toFixed(2)}</TableCell><TableCell>{row.hourly_rate.toFixed(2)}</TableCell><TableCell>{row.tracked_hours.toFixed(2)}</TableCell><TableCell>{row.overtime_hours.toFixed(2)}</TableCell><TableCell>{row.deduction_hours.toFixed(2)}</TableCell><TableCell>{row.deduction_amount.toFixed(2)}</TableCell><TableCell className="font-semibold">{row.net_salary.toFixed(2)}</TableCell><TableCell>{row.match_status === 'matched' ? <Badge><UserCheck className="h-3 w-3 mr-1" />Matched</Badge> : <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Unmatched</Badge>}</TableCell><TableCell><Button variant="outline" size="sm" disabled={!row.user_id} onClick={() => onTimesheet(row.user_id)}>View</Button></TableCell></TableRow>)}</TableBody></Table></div>;
}
